import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import CustomerTable from "../components/CustomerTable";
import AddCustomerModal from "../components/AddCustomerModal";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { Search, Filter, Plus, Users, Calendar, X } from "lucide-react";
import { customerService } from "../api/customerService";
import { reportService } from "../api/reportService";
import { useNotification } from "../contexts/NotificationContext";
import { HasPermission } from "../hooks/usePermission";

export default function Customers() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const getSavedCustomersState = () => {
    try {
      const raw = sessionStorage.getItem("customers_page_state");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };
  const savedState = getSavedCustomersState() || {};

  const [searchTerm, setSearchTerm] = useState(savedState.searchTerm || "");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Table State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(savedState.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(savedState.itemsPerPage || 10);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [customers, setCustomers] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const restoredScrollRef = React.useRef(savedState.scrollY || 0);

  // Persist state
  useEffect(() => {
    try {
      sessionStorage.setItem(
        "customers_page_state",
        JSON.stringify({
          searchTerm,
          currentPage,
          itemsPerPage,
          scrollY: window.scrollY || 0,
        })
      );
    } catch {}
  }, [searchTerm, currentPage, itemsPerPage]);

  useEffect(() => {
    const handleScroll = () => {
      try {
        const raw = sessionStorage.getItem("customers_page_state");
        const existing = raw ? JSON.parse(raw) : {};
        existing.scrollY = window.scrollY || 0;
        sessionStorage.setItem("customers_page_state", JSON.stringify(existing));
      } catch {}
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Restore scroll
  useEffect(() => {
    if (!loading && restoredScrollRef.current > 0) {
      const targetY = restoredScrollRef.current;
      restoredScrollRef.current = 0;
      setTimeout(() => {
        window.scrollTo({ top: targetY, behavior: "instant" });
      }, 60);
    }
  }, [loading]);

  // Fetch customers from API
  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await customerService.list({
        query: searchTerm,
        page: currentPage,
        limit: itemsPerPage,
      });

      setCustomers(response.data);
      setTotalData(response.meta?.total || response.data.length);
    } catch (err) {
      console.error("Error fetching customers:", err);
      setError(err.response?.data?.message || "Gagal memuat data pelanggan");
      showNotification("error", "Gagal memuat data pelanggan");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, itemsPerPage, searchTerm, refreshTrigger]);

  // Client-side sorting (if needed, can be moved to API later)
  const sortedCustomers = [...customers].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];
    if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
    if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  // Pagination Logic
  const totalEntries = totalData;
  const maxPages = Math.ceil(totalEntries / itemsPerPage);
  const startEntry = (currentPage - 1) * itemsPerPage + 1;
  const endEntry = Math.min(currentPage * itemsPerPage, totalEntries);

  const handleAddNew = () => {
    setSelectedCustomer(null);
    setIsAddModalOpen(true);
  };

  const handleEdit = (id) => {
    const customer = customers.find((c) => c.id === id);
    setSelectedCustomer(customer);
    setIsEditModalOpen(true);
  };

  const handleDelete = (id) => {
    const customer = customers.find((c) => c.id === id);
    setSelectedCustomer(customer);
    setIsDeleteModalOpen(true);
  };

  const handleAddSubmit = async (data) => {
    try {
      await customerService.create(data);
      showNotification("success", "Pelanggan berhasil ditambahkan");
      setIsAddModalOpen(false);
      fetchCustomers(); // Refresh data
    } catch (err) {
      console.error("Error creating customer:", err);
      showNotification(
        "error",
        err.response?.data?.message || "Gagal menambahkan pelanggan",
      );
    }
  };

  const handleEditSubmit = async (data) => {
    try {
      await customerService.update(selectedCustomer.id, data);
      showNotification("success", "Pelanggan berhasil diperbarui");
      setIsEditModalOpen(false);
      fetchCustomers(); // Refresh data
    } catch (err) {
      console.error("Error updating customer:", err);
      showNotification(
        "error",
        err.response?.data?.message || "Gagal memperbarui pelanggan",
      );
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await customerService.delete(selectedCustomer.id);
      showNotification("success", "Pelanggan berhasil dihapus");
      setIsDeleteModalOpen(false);
      fetchCustomers(); // Refresh data
    } catch (err) {
      console.error("Error deleting customer:", err);
      showNotification(
        "error",
        err.response?.data?.message || "Gagal menghapus pelanggan",
      );
    }
  };

  const handleInfo = (id) => {
    // Navigate to detail page
    navigate(`/pelanggan/${id}`);
  };

  return (
    <div className="flex min-h-screen font-sans bg-slate-50">
      <div
        className={`fixed inset-y-0 left-0 z-50 transition-all duration-300 pointer-events-none md:pointer-events-auto ${isSidebarCollapsed ? "w-16" : "w-48"}`}>
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={() =>
            setIsSidebarCollapsed((prev) => {
              const newState = !prev;
              localStorage.setItem(
                "sidebarCollapsed",
                JSON.stringify(newState),
              );
              return newState;
            })
          }
          activePage="pelanggan"
        />
      </div>

      <div
        className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-48"}`}>
        <Header
          isSidebarCollapsed={isSidebarCollapsed}
          onToggleSidebar={() =>
            setIsSidebarCollapsed((prev) => {
              const newState = !prev;
              localStorage.setItem(
                "sidebarCollapsed",
                JSON.stringify(newState),
              );
              return newState;
            })
          }
        />

        <main className="flex-1 p-6 overflow-x-hidden overflow-y-auto">
          {/* Top Card: Page Title & Actions */}
          <div className="p-5 mb-6 bg-white shadow-sm rounded-2xl ring-1 ring-slate-200/70">
            <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
              {/* Left: Page Title */}
              <div className="flex items-center w-full gap-4 md:w-auto">
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-teal-50 shrink-0">
                  <Users className="w-6 h-6 text-teal-600" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-xl font-bold text-gray-900">
                    Manajemen Pelanggan
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Kelola data pelanggan toko Anda
                  </p>
                </div>
              </div>

              {/* Right: Actions */}
              <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto">
                <div className="relative flex-1 md:w-48">
                  <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Cari data . . ."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full py-2 pr-4 text-sm transition-colors border rounded-lg pl-9 bg-slate-50 border-slate-200 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 focus:bg-white"
                  />
                </div>



                <HasPermission permission="customer.create">
                  <button
                    onClick={handleAddNew}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-colors bg-teal-600 rounded-lg shadow-sm hover:bg-teal-700 whitespace-nowrap shadow-teal-600/20">
                    <Plus className="w-4 h-4" />
                    <span className="hidden md:inline">Tambah</span>
                  </button>
                </HasPermission>
              </div>
            </div>
          </div>

          <CustomerTable
            customers={sortedCustomers} // Using sorted customers directly as mock API returns limited data
            searchTerm={searchTerm}
            sortBy={sortBy}
            sortOrder={sortOrder}
            loading={loading}
            error={error}
            totalEntries={totalEntries}
            currentPage={currentPage}
            maxPages={maxPages}
            startEntry={startEntry}
            endEntry={endEntry}
            itemsPerPage={itemsPerPage}
            onSearch={(val) => {
              setSearchTerm(val);
              setCurrentPage(1);
            }}
            onSort={(column) => {
              if (sortBy === column) {
                setSortOrder(sortOrder === "asc" ? "desc" : "asc");
              } else {
                setSortBy(column);
                setSortOrder("asc");
              }
            }}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onInfo={handleInfo}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(newPerPage) => {
              setItemsPerPage(newPerPage);
              setCurrentPage(1);
            }}
          />
        </main>
      </div>

      <AddCustomerModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddSubmit}
      />

      <AddCustomerModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditSubmit}
        initialData={selectedCustomer}
      />

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        data={selectedCustomer}
      />
    </div>
  );
}
